import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import { PageHeader } from "@/components/app/page-header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Save, FileDown, PenTool, Plus, Loader2, Eye, Pencil, Wand2, ChevronDown, Menu, MoreVertical } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { TipTapEditor } from "@/components/drafting/tiptap-editor";
import { RevisionsPanel } from "@/components/drafting/revisions-panel";
import { aiContinueDraft, aiReviseDraft } from "@/lib/drafting.functions";
import { exportDraftPdf } from "@/lib/pdf.functions";
import { exportDraftDocx } from "@/lib/docx.functions";
import { logActivity } from "@/lib/activity.functions";
import { useServerFn } from "@tanstack/react-start";

const DraftingSearchSchema = z.object({ prompt: z.string().optional() });

export const Route = createFileRoute("/_authenticated/app/drafting")({
  validateSearch: (s) => DraftingSearchSchema.parse(s),
  component: DraftingPage,
});

type Draft = { id: string; title: string; content: string | null; act_type: string | null; updated_at: string };

const REVISE_PRESETS: { labelKey: string; instruction: string }[] = [
  { labelKey: "clarity", instruction: "Riscrivi migliorando chiarezza e leggibilità, mantenendo il registro forense." },
  { labelKey: "formal", instruction: "Rendi il tono più formale e tecnico, in italiano forense." },
  { labelKey: "shorten", instruction: "Sintetizza il testo riducendone la lunghezza del 30-40%, senza perdere contenuti giuridici essenziali." },
  { labelKey: "expand", instruction: "Espandi l'argomentazione giuridica con maggiore profondità, mantenendo coerenza con i fatti citati." },
  { labelKey: "grammar", instruction: "Correggi soltanto errori di grammatica, sintassi e punteggiatura, senza alterare il contenuto." },
];

function DraftingPage() {
  const { t } = useTranslation();
  const [drafts, setDrafts] = useState<Draft[]>([]);
  const [active, setActive] = useState<Draft | null>(null);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [savingLabel, setSavingLabel] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [reviseLoading, setReviseLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [preview, setPreview] = useState(false);
  const editorRef = useRef<any>(null);
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const aiContinueFn = useServerFn(aiContinueDraft);
  const aiReviseFn = useServerFn(aiReviseDraft);
  const exportPdfFn = useServerFn(exportDraftPdf);
  const exportDocxFn = useServerFn(exportDraftDocx);
  const logFn = useServerFn(logActivity);

  const downloadBase64 = (base64: string, filename: string, mimeType: string) => {
    const bin = atob(base64);
    const arr = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
    const blob = new Blob([arr], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleExportPdf = async () => {
    if (!active) return;
    setExporting(true);
    try {
      await persist(title, content);
      const res = await exportPdfFn({ data: { draftId: active.id } });
      if (!res.ok) throw new Error(t("drafting.exportFailed"));
      downloadBase64(res.base64, res.filename, res.mimeType);
      await logFn({ data: { action: "draft.export.pdf", entity: "draft", entity_id: active.id } }).catch(() => {});
      toast.success(t("drafting.pdfGenerated"));
    } catch (e: any) {
      toast.error(e?.message ?? t("drafting.exportPdfError"));
    } finally {
      setExporting(false);
    }
  };

  const handleExportDocx = async () => {
    if (!active) return;
    setExporting(true);
    try {
      await persist(title, content);
      const res = await exportDocxFn({ data: { draftId: active.id } });
      if (!res.ok) throw new Error(t("drafting.exportFailed"));
      downloadBase64(res.base64, res.filename, res.mimeType);
      await logFn({ data: { action: "draft.export.docx", entity: "draft", entity_id: active.id } }).catch(() => {});
      toast.success(t("drafting.docxGenerated"));
    } catch (e: any) {
      toast.error(e?.message ?? t("drafting.exportDocxError"));
    } finally {
      setExporting(false);
    }
  };

  const load = async () => {
    const { data } = await supabase
      .from("drafts")
      .select("id, title, content, act_type, updated_at")
      .order("updated_at", { ascending: false });
    setDrafts((data ?? []) as Draft[]);
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (active) {
      setTitle(active.title);
      setContent(active.content ?? "");
      setSavingLabel("");
    }
  }, [active?.id]);

  const newDraft = async () => {
    const { data: u } = await supabase.auth.getUser();
    if (!u.user) return;
    const { data, error } = await supabase
      .from("drafts")
      .insert({ owner_id: u.user.id, title: t("drafting.newDraft"), content: "" })
      .select()
      .single();
    if (error) return toast.error(error.message);
    await load();
    setActive(data as Draft);
  };

  const persist = async (newTitle: string, c: string) => {
    if (!active) return;
    setSavingLabel(t("drafting.saving"));
    const { error } = await supabase.from("drafts").update({ title: newTitle, content: c }).eq("id", active.id);
    if (error) { setSavingLabel(t("drafting.savingError")); return toast.error(error.message); }
    setSavingLabel(t("drafting.saved"));
  };

  // Debounced autosave
  useEffect(() => {
    if (!active) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => persist(title, content), 1500);
    return () => { if (saveTimer.current) clearTimeout(saveTimer.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, content]);

  const handleAi = async (editor: any) => {
    if (!editor) return;
    editorRef.current = editor;
    const text = editor.getText().slice(-6000);
    if (!text.trim()) return toast.error(t("drafting.nothingToContinue"));
    setAiLoading(true);
    try {
      const { html, error } = await aiContinueFn({ data: { context: text, actType: active?.act_type ?? undefined } });
      if (error) toast.error(error);
      else if (html) editor.chain().focus().insertContent(html).run();
    } catch (e: any) {
      toast.error(e?.message ?? t("drafting.aiError"));
    } finally { setAiLoading(false); }
  };

  const handleRevise = async (instruction: string) => {
    const editor = editorRef.current;
    if (!editor) return toast.error(t("drafting.openEditorFirst"));
    const sel = editor.state.selection;
    const hasSelection = !sel.empty;
    const target = hasSelection
      ? editor.state.doc.textBetween(sel.from, sel.to, "\n")
      : editor.getText();
    if (!target.trim()) return toast.error(t("drafting.selectOrWrite"));
    setReviseLoading(true);
    try {
      const { html, error } = await aiReviseFn({
        data: { context: target.slice(0, 18000), instruction, actType: active?.act_type ?? undefined },
      });
      if (error) return toast.error(error);
      if (!html) return;
      if (hasSelection) {
        editor.chain().focus().deleteSelection().insertContent(html).run();
      } else {
        editor.chain().focus().setContent(html).run();
        setContent(editor.getHTML());
      }
      toast.success(t("drafting.revised"));
    } catch (e: any) {
      toast.error(e?.message ?? t("drafting.reviseError"));
    } finally { setReviseLoading(false); }
  };

  const draftsList = (
    <>
      <Button onClick={newDraft} className="w-full justify-start gap-2" variant="outline">
        <Plus className="h-4 w-4" /> {t("drafting.newDraft")}
      </Button>
      <div className="mt-4 space-y-1 overflow-auto">
        {drafts.map((d) => (
          <button
            key={d.id}
            onClick={() => setActive(d)}
            className={cn(
              "relative block w-full truncate rounded-md px-3 py-2 text-left text-sm transition-colors",
              active?.id === d.id
                ? "bg-muted text-foreground before:absolute before:left-0 before:top-2 before:h-[calc(100%-1rem)] before:w-0.5 before:rounded-r before:bg-primary"
                : "text-muted-foreground hover:bg-muted/60 hover:text-foreground"
            )}
          >
            {d.title}
          </button>
        ))}
        {drafts.length === 0 && <p className="px-3 py-2 text-xs text-muted-foreground">{t("drafting.noDrafts")}</p>}
      </div>
    </>
  );

  return (
    <div className="flex h-[calc(100dvh-3.5rem)] md:h-[calc(100vh-4rem)]">
      <aside className="hidden w-64 shrink-0 border-r bg-muted/30 p-3 lg:block">{draftsList}</aside>

      <div className="flex flex-1 flex-col">
        <PageHeader
          title={t("drafting.pageTitle")}
          description={t("drafting.pageDescription")}
          actions={
            active && (
              <>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm" disabled={reviseLoading}>
                      {reviseLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Wand2 className="mr-2 h-4 w-4" />}
                      {t("drafting.revise")} <ChevronDown className="ml-1 h-3 w-3" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-64">
                    {REVISE_PRESETS.map((p) => (
                      <DropdownMenuItem key={p.labelKey} onClick={() => handleRevise(p.instruction)}>
                        {t(`drafting.revisePresets.${p.labelKey}`)}
                      </DropdownMenuItem>
                    ))}
                    <DropdownMenuItem
                      onClick={() => {
                        const i = window.prompt(t("drafting.customInstructionPrompt"));
                        if (i && i.trim()) handleRevise(i.trim());
                      }}
                    >
                      {t("drafting.customInstruction")}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                <RevisionsPanel
                  draftId={active.id}
                  currentContent={content}
                  onRestored={(html) => {
                    setContent(html);
                    editorRef.current?.commands.setContent(html, { emitUpdate: false });
                  }}
                />
                <Button variant="outline" size="sm" onClick={() => setPreview((v) => !v)}>
                  {preview ? <Pencil className="mr-2 h-4 w-4" /> : <Eye className="mr-2 h-4 w-4" />}
                  {preview ? t("drafting.edit") : t("drafting.preview")}
                </Button>
                <Button variant="outline" size="sm" onClick={handleExportDocx} disabled={exporting}>
                  {exporting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileDown className="mr-2 h-4 w-4" />}
                  {t("drafting.docx")}
                </Button>
                <Button variant="outline" size="sm" onClick={handleExportPdf} disabled={exporting}>
                  {exporting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileDown className="mr-2 h-4 w-4" />}
                  {t("drafting.pdf")}
                </Button>
                <Button size="sm" onClick={() => persist(title, content)}>
                  <Save className="mr-2 h-4 w-4" /> {t("drafting.save")}
                </Button>
              </>
            )
          }
        />

        {!active ? (
          <div className="flex-1 overflow-auto p-6">
            <Card className="flex flex-col items-center justify-center p-12 text-center">
              <PenTool className="mb-4 h-10 w-10 text-muted-foreground" />
              <h3 className="font-display text-xl font-semibold">{t("drafting.startTitle")}</h3>
              <p className="mt-1 text-sm text-muted-foreground">{t("drafting.startDesc")}</p>
              <Button className="mt-4" onClick={newDraft}>{t("drafting.newDraft")}</Button>
            </Card>
          </div>
        ) : (
          <div className="flex flex-1 flex-col overflow-hidden">
            {/* Mobile action bar */}
            <div className="flex items-center gap-2 border-b bg-card px-2 py-2 md:hidden print:hidden">
              <Sheet>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="icon" aria-label={t("drafting.drafts")}><Menu className="h-5 w-5" /></Button>
                </SheetTrigger>
                <SheetContent side="left" className="w-72 p-3">
                  <SheetHeader><SheetTitle>{t("drafting.drafts")}</SheetTitle></SheetHeader>
                  <div className="mt-3">{draftsList}</div>
                </SheetContent>
              </Sheet>
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="h-9 flex-1 border-none bg-transparent text-base font-semibold shadow-none focus-visible:ring-0"
                placeholder={t("drafting.titlePlaceholder")}
                disabled={preview}
              />
              <Button size="icon" variant="ghost" onClick={() => persist(title, content)} aria-label={t("drafting.save")}>
                <Save className="h-5 w-5" />
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button size="icon" variant="ghost" aria-label={t("drafting.more")}><MoreVertical className="h-5 w-5" /></Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuItem onClick={() => setPreview((v) => !v)}>
                    {preview ? <Pencil className="mr-2 h-4 w-4" /> : <Eye className="mr-2 h-4 w-4" />}
                    {preview ? t("drafting.edit") : t("drafting.preview")}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleExportDocx} disabled={exporting}>
                    <FileDown className="mr-2 h-4 w-4" /> {t("drafting.exportDocx")}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleExportPdf} disabled={exporting}>
                    <FileDown className="mr-2 h-4 w-4" /> {t("drafting.exportPdf")}
                  </DropdownMenuItem>
                  {REVISE_PRESETS.map((p) => (
                    <DropdownMenuItem key={p.labelKey} onClick={() => handleRevise(p.instruction)}>
                      <Wand2 className="mr-2 h-4 w-4" /> {t(`drafting.revisePresets.${p.labelKey}`)}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            {/* Desktop title */}
            <div className="hidden border-b bg-card px-6 py-3 md:block print:hidden">
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="border-none bg-transparent font-display text-2xl font-semibold shadow-none focus-visible:ring-0"
                placeholder={t("drafting.titlePlaceholderFull")}
                disabled={preview}
              />
            </div>
            <div className="flex-1 overflow-hidden">
              {preview ? (
                <div className="h-full overflow-auto bg-muted/30 p-2 sm:p-6">
                  <div
                    className="tiptap-doc tiptap-print mx-auto w-full max-w-full rounded-md bg-card px-4 py-5 shadow-sm ring-1 ring-border sm:max-w-[210mm] sm:px-[25mm] sm:py-[20mm] sm:shadow-lg"
                    dangerouslySetInnerHTML={{ __html: content || `<p><em>${t("drafting.emptyDoc")}</em></p>` }}
                  />
                </div>
              ) : (
                <TipTapEditor
                  value={content}
                  onChange={setContent}
                  onAiContinue={handleAi}
                  aiLoading={aiLoading}
                  savingLabel={savingLabel}
                />
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
